import { QueryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { APITypes, Dynamo } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import { getIntl } from '../../utils/intl';
import urls from '../../utils/urls';
import { getUsageMetricTypeCacheKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('useMedicalProfileUsageMetric', {
    failedToLoadUsage: 'Failed to load usage',
    'file-upload': '{used}/{allowed} MB Loaded /Mo.',
    'audio-file-upload': '{used}/{allowed} Recordings /Mo.',
    'portal-connect': '{used}/{allowed} Refreshes /Mo.',
    'fasten-refresh': '{used}/{allowed} Refreshes /Mo.',
    'chat-message': '{used}/{allowed} Chats /Mo.',
});

export type UseMedicalProfileUsageMetricArgs = {
    medicalProfileId: string;
    metric: Dynamo.MedicalProfileUsageMetricType;
};

export type UseMedicalProfileUsageMetricReturnValue = {
    isLoadingMetric: boolean;
    used: number;
    allowed: number | 'unlimited';
    hasReachedLimit: boolean;
};

export type GetLimitMessageArgs = {
    used: number;
    allowed: number;
    metric: Dynamo.MedicalProfileUsageMetricType;
};

const bytesToMegabytes = (bytes: number) => {
    return Math.ceil(bytes / 1024 / 1024);
};

export const getLimitMessage = ({ used, allowed, metric }: GetLimitMessageArgs) => {
    const message = intl(metric, {
        used: metric === 'file-upload' ? bytesToMegabytes(used) : used,
        allowed: metric === 'file-upload' ? bytesToMegabytes(allowed) : allowed,
    });

    return message;
};

const getHasReachedLimit = (
    isLoading: boolean,
    data: APITypes.usageService.GetMedicalProfileUsageMetricStatusResponse | undefined,
) => {
    let hasReachedLimit = true;
    if (!isLoading && data && (data.allowed === 'unlimited' || data.used < data.allowed)) {
        hasReachedLimit = false;
    }

    return hasReachedLimit;
};

export const useMedicalProfileUsageMetric = ({
    medicalProfileId,
    metric,
}: UseMedicalProfileUsageMetricArgs): UseMedicalProfileUsageMetricReturnValue => {
    const myOrganizationClientInvitationsQuery = useQuery({
        queryKey: getUsageMetricTypeCacheKey(medicalProfileId, metric),
        queryFn: async () => {
            return await appAPI.get<APITypes.usageService.GetMedicalProfileUsageMetricStatusResponse>(
                urls.api.organizations.usage.profileMetricUsage(medicalProfileId, metric),
            );
        },
    });
    useQueryErrorNotify(myOrganizationClientInvitationsQuery, intl('failedToLoadUsage'));

    const hasReachedLimit = getHasReachedLimit(
        myOrganizationClientInvitationsQuery.isLoading,
        myOrganizationClientInvitationsQuery.data,
    );

    return {
        isLoadingMetric: myOrganizationClientInvitationsQuery.isLoading,
        used: myOrganizationClientInvitationsQuery.data?.used ?? 0,
        allowed: myOrganizationClientInvitationsQuery.data?.allowed ?? 0,
        hasReachedLimit: hasReachedLimit,
    };
};

export const useHasReachedFileUploadLimit = (medicalProfileId: string): boolean => {
    return useMedicalProfileUsageMetric({
        medicalProfileId,
        metric: 'file-upload',
    }).hasReachedLimit;
};

export const useHasReachedAudioFileUploadLimit = (medicalProfileId: string): boolean => {
    return useMedicalProfileUsageMetric({
        medicalProfileId,
        metric: 'audio-file-upload',
    }).hasReachedLimit;
};

export const useHasReachedPortalConnectLimit = (medicalProfileId: string): boolean => {
    return useMedicalProfileUsageMetric({
        medicalProfileId,
        metric: 'portal-connect',
    }).hasReachedLimit;
};

export const useHasReachedChatMessageLimit = (medicalProfileId: string): boolean => {
    return useMedicalProfileUsageMetric({
        medicalProfileId,
        metric: 'chat-message',
    }).hasReachedLimit;
};

export type UseProfilesWithFileLimitsArgs = {
    medicalProfileIds: string[];
};

export type ProfileFileLimit = {
    medicalProfileId: string;
    hasReachedFileUploadLimit: boolean;
};

export type UseProfilesWithFileLimitsReturnValue = {
    profilesWithFileLimits: ProfileFileLimit[];
    isLoading: boolean;
};

export const useProfilesWithFileLimits = ({
    medicalProfileIds,
}: UseProfilesWithFileLimitsArgs): UseProfilesWithFileLimitsReturnValue => {
    const queries = medicalProfileIds.map<
        QueryOptions<APITypes.usageService.GetMedicalProfileUsageMetricStatusResponse>
    >((medicalProfileId) => ({
        queryKey: getUsageMetricTypeCacheKey(medicalProfileId, 'file-upload'),
        queryFn: () =>
            appAPI.get<APITypes.usageService.GetMedicalProfileUsageMetricStatusResponse>(
                urls.api.organizations.usage.profileMetricUsage(medicalProfileId, 'file-upload'),
            ),
    }));
    const queryResults = useQueries({ queries: queries as any });

    const profilesWithFileLimits = queryResults.map((queryResult, index) => ({
        medicalProfileId: medicalProfileIds[index],
        hasReachedFileUploadLimit: getHasReachedLimit(
            queryResult.isLoading,
            queryResult.data as APITypes.usageService.GetMedicalProfileUsageMetricStatusResponse | undefined,
        ),
    }));

    return {
        profilesWithFileLimits,
        isLoading: queryResults.some((queryResult) => queryResult.isLoading),
    };
};
